import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PlatformIcon from '../components/PlatformIcon';
import './AssistantConversations.css';

const formatAbsolute = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatRelative = (value) => {
  if (!value) return 'never';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 'unknown';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const userLabel = (conv) => {
  const parts = [conv.userName, conv.userSurname].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Anonymous';
};

const renderInline = (text) => {
  const nodes = [];
  const re = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|\[[^\]]+\]\([^)]+\))/g;
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) nodes.push(text.slice(lastIdx, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      nodes.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      nodes.push(<code key={key++}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('[')) {
      const linkMatch = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        nodes.push(
          <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
            {linkMatch[1]}
          </a>,
        );
      } else {
        nodes.push(tok);
      }
    } else {
      nodes.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    }
    lastIdx = m.index + tok.length;
  }
  if (lastIdx < text.length) nodes.push(text.slice(lastIdx));
  return nodes;
};

const Markdown = ({ text }) => {
  if (!text) return <em style={{ color: '#9ca3af' }}>(empty)</em>;
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    const fence = line.match(/^```(\w*)$/);
    if (fence) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].match(/^```$/)) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;
      blocks.push(
        <pre key={blocks.length} className="assistant-md-pre"><code>{codeLines.join('\n')}</code></pre>,
      );
      continue;
    }

    const h = line.match(/^(#{1,6})\s+(.+)$/);
    if (h) {
      const level = Math.min(6, h[1].length + 2);
      const Tag = `h${level}`;
      blocks.push(<Tag key={blocks.length} className="assistant-md-h">{renderInline(h[2])}</Tag>);
      i++;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={blocks.length} className="assistant-md-ul">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>,
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={blocks.length} className="assistant-md-ol">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ol>,
      );
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^#{1,6}\s+/.test(lines[i]) &&
      !/^```/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={blocks.length} className="assistant-md-p">
        {paraLines.map((l, j) => (
          <React.Fragment key={j}>
            {renderInline(l)}
            {j < paraLines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>,
    );
  }
  return <div className="assistant-md">{blocks}</div>;
};

const MessageMeta = ({ metadata }) => {
  const [open, setOpen] = useState(false);
  if (!metadata) return null;
  const tokens = metadata.tokens || {};
  const promptTokens = tokens.prompt ?? tokens.prompt_tokens;
  const completionTokens = tokens.completion ?? tokens.completion_tokens;
  const totalTokens =
    tokens.total ?? tokens.total_tokens ??
    (typeof promptTokens === 'number' && typeof completionTokens === 'number'
      ? promptTokens + completionTokens
      : undefined);

  return (
    <>
      <button className="assistant-msg-meta-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide metadata' : 'Show metadata'}
      </button>
      {open && (
        <div className="assistant-msg-meta">
          <div className="assistant-msg-meta-grid">
            <div><span>Model:</span>{metadata.model ?? '—'}</div>
            <div><span>Prompt tok:</span>{promptTokens ?? '—'}</div>
            <div><span>Completion tok:</span>{completionTokens ?? '—'}</div>
            <div><span>Total tok:</span>{totalTokens ?? '—'}</div>
            <div><span>Latency:</span>{metadata.latency_ms != null ? `${metadata.latency_ms} ms` : '—'}</div>
            <div><span>Finish:</span>{metadata.finish_reason ?? '—'}</div>
          </div>
          <pre>{JSON.stringify(metadata, null, 2)}</pre>
        </div>
      )}
    </>
  );
};

const AssistantConversationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const { fetchWithAuth } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchWithAuth(`/api/admin/assistant/conversations/${id}`);
      if (!resp.ok) throw new Error('Failed to load conversation');
      const data = await resp.json();
      setConv(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, id]);

  useEffect(() => {
    load();
  }, [load]);

  const copyAsText = async () => {
    if (!conv) return;
    const text = conv.messages
      .map((m) => `[${m.role} @ ${formatAbsolute(m.timestamp)}]\n${m.content || ''}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore
    }
  };

  if (loading) return <div className="loading">Loading conversation...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!conv) return <div className="error">Conversation not found.</div>;

  const systemMessages = conv.messages.filter((m) => m.role === 'system');
  const otherMessages = conv.messages.filter((m) => m.role !== 'system');

  return (
    <div>
      <div className="assistant-detail-header">
        <div className="assistant-detail-title-row">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="assistant-back-btn" onClick={() => navigate(-1)}>← Back</button>
            <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
              {conv.title || <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>&lt;untitled&gt;</span>}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={copyAsText}>
              {copied ? 'Copied!' : 'Copy as text'}
            </button>
          </div>
        </div>
        <div className="assistant-detail-meta">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            User:{' '}
            <Link className="link" to={`/assistant?userId=${conv.userId}`}>
              {userLabel(conv)}
            </Link>
            <PlatformIcon platform={conv.userPlatform} />
            {conv.userEmail && <span style={{ color: '#9ca3af' }}>({conv.userEmail})</span>}
          </span>
          <span>{conv.messageCount.toLocaleString()} messages</span>
          <span title={formatAbsolute(conv.createdAt)}>
            Created: {formatAbsolute(conv.createdAt)}
          </span>
          <span title={formatAbsolute(conv.lastMessageAt)}>
            Last message: {formatRelative(conv.lastMessageAt)}
          </span>
        </div>
      </div>

      {systemMessages.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#78350f', marginBottom: 8 }}>
            System prompt{systemMessages.length > 1 ? 's' : ''}
          </div>
          {systemMessages.map((m) => (
            <div key={m.id} className="assistant-msg assistant-msg-system">
              {m.content}
            </div>
          ))}
        </div>
      )}

      <div className="card">
        {otherMessages.length === 0 ? (
          <div className="assistant-empty">No messages logged for this conversation.</div>
        ) : (
          <div className="assistant-chat">
            {otherMessages.map((m) => {
              const isUser = m.role === 'user';
              return (
                <div
                  key={m.id}
                  className={`assistant-msg ${isUser ? 'assistant-msg-user' : 'assistant-msg-assistant'}`}
                >
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                    {isUser ? 'USER' : 'ASSISTANT'}
                  </div>
                  <div>
                    {isUser
                      ? (m.content || <em style={{ color: '#9ca3af' }}>(empty)</em>)
                      : <Markdown text={m.content} />
                    }
                  </div>
                  <div className="assistant-msg-footer">
                    <span title={formatAbsolute(m.timestamp)}>{formatRelative(m.timestamp)}</span>
                    {!isUser && m.metadata && (
                      <MessageMeta metadata={m.metadata} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantConversationDetail;
